// import { ShepherdTour } from 'react-shepherd'; // ShepherdTour is used in TutorialContext directly
import { navbarTutorialSteps } from './tutorialSteps'; // Import the specific steps
// import { useContext } from 'react'; // Not directly used here anymore
// import { TutorialContext } from './TutorialContext'; // Not directly used here anymore

// Basic Shepherd options
const defaultStepOptions = { // Updated to minimal working config
  classes: 'shepherd-theme-arrows',
  scrollTo: true,
  cancelIcon: {
    enabled: true,
  },
  highlightClass: 'highlight', // CSS class for the highlighted element
  modalOverlayOpeningPadding: 4,
  modalOverlayOpeningRadius: 4,
  // classes: 'shepherd-custom-theme', // A general class for all steps for easier theming
};

const tourOptions = {
  useModalOverlay: false, // Kept from previous minimal config
  defaultStepOptions: defaultStepOptions // Referencing updated minimal defaultStepOptions
};

// This function will initialize and configure a Shepherd tour instance.
// It will be called by TutorialProvider when startTutorial is invoked.
export const initializeTutorial = (themeMode = 'light', steps = navbarTutorialSteps) => { // Default to navbarTutorialSteps
  // Logic to dynamically apply styles for light/dark mode to the provided steps
  const themedSteps = steps.map(step => {
    // Ensure step.classes is a string and handle undefined or null
    const existingClasses = typeof step.classes === 'string' ? step.classes : '';
    return {
      ...step,
      classes: `${existingClasses} shepherd-theme-${themeMode}`.trim(),
    };
  });

  // No longer using placeholderSteps directly here, assuming `steps` (i.e., navbarTutorialSteps) is the primary input.
  // If navbarTutorialSteps is empty for some reason, the tour will be empty, which is fine.

  // This function now returns the array of step configurations, themed.
  // The ShepherdTour component in TutorialContext will use these.
  return themedSteps;
};

// Example of how the TutorialProvider might use this:
//
// import { ShepherdTour, ShepherdTourContext } from 'react-shepherd';
// import { initializeTutorial } from './ShepherdConfig';
//
// const AppWithTutorial = () => {
//   const { isTutorialActive, startTutorial, endTutorial } = useContext(TutorialContext);
//   const muiTheme = useTheme(); // Assuming you use MUI's useTheme hook
//
//   // This is a simplified example.
//   // You'll need to manage the ShepherdTour instance, possibly via ShepherdTourContext.
//   // react-shepherd's <ShepherdTour> component might be the primary way to manage this.
//
//   const tourSteps = initializeTutorial(muiTheme.palette.mode);
//
//   return (
//     <ShepherdTour steps={tourSteps} tourOptions={tourOptions}>
//       {/* App components */}
//       {/* <button onClick={startTutorial}>Start Tutorial</button> */}
//     </ShepherdTour>
//   );
// };

// The Shepherd instance itself will likely be managed by the <ShepherdTour> component from react-shepherd
// which we will integrate in App.js or a similar top-level component.
// This file primarily holds the configuration.
// The `initializeTutorial` function can be used to generate step configurations
// dynamically if needed, especially for theming.

export const getTourOptions = (themeMode = 'light') => {
  // Dynamically adjust tourOptions based on themeMode if necessary
  const dynamicStepOptions = { // These are options that will be merged into each step
    ...defaultStepOptions,
    // Add classes for theme-specific styling directly if not handled by initializeTutorial's step.classes
    // classes: `shepherd-theme-${themeMode}`, // This would apply to all steps if not overridden
  };


  // The tourOptions passed to ShepherdTour can include defaultStepOptions.
  // We can also add default buttons here if react-shepherd allows it,
  // but actions would need access to context methods (skipTutorial, markTutorialDisabled).
  // It's often cleaner to define these actions on the step definitions themselves or via tour events.

  // Example of how you might structure it if ShepherdJS allows global button definitions
  // with actions that can be wired up later. (This is conceptual for Shepherd.js core)
  // const builtInButtons = {
  //   skip: {
  //     text: 'Skip',
  //     action: 'skipAction', // Placeholder, would need to be mapped to context.skipTutorial
  //     secondary: true,
  //   },
  //   next: {
  //     text: 'Next',
  //     action: 'nextAction', // Placeholder for tour.next()
  //   },
  // };

  return {
    ...tourOptions, // Contains useModalOverlay: true
    defaultStepOptions: dynamicStepOptions, // Contains cancelIcon, highlightClass, etc.
                                          // Plus any theme-specific classes if added here
    // exitOnEsc: true, // Example Shepherd option
    // keyboardNavigation: true, // Example Shepherd option
  };
};
